import { assertNever, BaseNumber, CheckedAddress, NormalizedNumber, promiseAll } from '@sparkdotfi/common-universal'
import { queryOptions } from '@tanstack/react-query'
import { reduce } from 'remeda'
import { erc20Abi } from 'viem'
import { Config } from 'wagmi'
import { readContract } from 'wagmi/actions'

import { skySavingsBackedByFunction } from './skySavingsBackedByQuery'
import { getChainConfigEntry } from '@/config/chain'
import { BackedByQueryResult, SavingsBackedByData } from '@/config/chain/types'
import { sparkVaultV2Abi } from '@/config/contracts-generated'
import { TokenSymbol } from '@/domain/types/TokenSymbol'

export function sparkVaultV2StableBackedByQueryOptionsFactory(type: 'spusdc' | 'spusdt' | 'sppyusd') {
  const { savingsTokenSymbol, underlyingTokenSymbol } = getConfig(type)

  return function sparkVaultV2StableBackedByQueryOptions({
    chainId,
    wagmiConfig,
  }: {
    chainId: number
    wagmiConfig: Config
  }): BackedByQueryResult {
    return {
      queryOptions: queryOptions({
        queryKey: sparkVaultV2StableBackedByQueryKey(savingsTokenSymbol, chainId),
        queryFn: () => {
          const { tokenDefinitionRepository } = getChainConfigEntry(chainId)
          const savingsToken = tokenDefinitionRepository.findOneBySymbol(savingsTokenSymbol)
          const underlyingToken = tokenDefinitionRepository.findOneBySymbol(underlyingTokenSymbol)
          return sparkVaultV2StableBackedByQueryFunction({
            savingsTokenAddress: savingsToken.address,
            underlyingTokenAddress: underlyingToken.address,
            decimals: underlyingToken.decimals,
            chainId,
            wagmiConfig,
          })
        },
      }),
      groupUsdsDetails: true,
    }
  }
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function sparkVaultV2StableBackedByQueryKey(savingsTokenSymbol: TokenSymbol, chainId: number) {
  return ['spark-vault-v2-stable-backed-by', savingsTokenSymbol, chainId]
}

export async function sparkVaultV2StableBackedByQueryFunction({
  savingsTokenAddress,
  underlyingTokenAddress,
  decimals,
  chainId,
  wagmiConfig,
}: {
  savingsTokenAddress: CheckedAddress
  underlyingTokenAddress: CheckedAddress
  decimals: number
  chainId: number
  wagmiConfig: Config
}): Promise<SavingsBackedByData> {
  const { susdsBackedByData, _totalAssets, _liquidity } = await promiseAll(
    {
      susdsBackedByData: skySavingsBackedByFunction,
      _totalAssets: () =>
        readContract(wagmiConfig, {
          address: savingsTokenAddress,
          abi: sparkVaultV2Abi,
          functionName: 'totalAssets',
          chainId,
        }),
      _liquidity: () =>
        readContract(wagmiConfig, {
          address: underlyingTokenAddress,
          abi: erc20Abi,
          functionName: 'balanceOf',
          args: [savingsTokenAddress],
          chainId,
        }),
    },
    { concurrency: 'unbounded' },
  )

  const totalAssets = BaseNumber(_totalAssets).toNormalizedUnit(decimals)
  const liquidity = BaseNumber(_liquidity).toNormalizedUnit(decimals)

  const usdsAmount = NormalizedNumber.max(totalAssets.minus(liquidity), NormalizedNumber.ZERO)
  const susdsTotalAssets = reduce(susdsBackedByData, (acc, item) => acc.plus(item.amount), NormalizedNumber.ZERO)
  const scaleFactor = susdsTotalAssets.eq(NormalizedNumber.ZERO)
    ? NormalizedNumber.ZERO
    : usdsAmount.div(susdsTotalAssets)

  const scaledItems: SavingsBackedByData = susdsBackedByData.map((item) => ({
    ...item,
    amount: item.amount.times(scaleFactor),
  }))

  return [...scaledItems, { category: 'idle', amount: liquidity }]
}

function getConfig(type: 'spusdc' | 'spusdt' | 'sppyusd'): {
  savingsTokenSymbol: TokenSymbol
  underlyingTokenSymbol: TokenSymbol
} {
  switch (type) {
    case 'spusdc':
      return {
        savingsTokenSymbol: TokenSymbol('spUSDC'),
        underlyingTokenSymbol: TokenSymbol('USDC'),
      }
    case 'spusdt':
      return {
        savingsTokenSymbol: TokenSymbol('spUSDT'),
        underlyingTokenSymbol: TokenSymbol('USDT'),
      }
    case 'sppyusd':
      return {
        savingsTokenSymbol: TokenSymbol('spPYUSD'),
        underlyingTokenSymbol: TokenSymbol('PYUSD'),
      }
    default:
      assertNever(type)
  }
}
